<template>
    <edit-resource
        :form="form"
        :title="title"
        :is-email-template="true"
        @close="$emit('close')"
        @submit="saveTemplate"
    >

        <div class="w-768">
            <toggle v-if="form.type === 'auto_reply'" v-model="form.default" :disabled="form.original.default" class="mb-16">
                {{ $t('settings.email-templates.auto_reply_default') }}
            </toggle>
            <translatable #default="{ languages, selectedLanguage }" :errors="warningTabs">
                <div v-for="lang in languages" :key="lang">

                    <simple-input v-show="lang === selectedLanguage" v-model="form.name[lang]" class="mb-16">
                        {{ $t('settings.email-templates.name') }} <span v-if="languages.length > 1">({{ lang }})</span>
                    </simple-input>

                    <simple-input v-show="lang === selectedLanguage" v-model="form.subject[lang]" class="mb-16">
                        {{ $t('settings.email-templates.subject') }}* <span v-if="languages.length > 1">({{
                            lang
                        }})</span>
                    </simple-input>

                    <wysiwyg
                        with-links
                        v-show="lang === selectedLanguage"
                        v-model="form.content[lang]"
                        :placeholders="form.type === 'interview' ? interviewPlaceholders.placeholdersInterviewInvitation : placeholders"
                    >
                        {{ $t('settings.email-templates.content') }} <span v-if="languages.length > 1">({{ lang }})</span>
                    </wysiwyg>


                   <template v-if="form.type === 'interview'">
                       <simple-input v-show="lang === selectedLanguage" v-model="form.confirmation_subject[lang]" class="mt-32 mb-16">
                           {{ $t('interview.confirmation.subject') }} <span v-if="languages.length > 1">({{ lang }})</span>
                       </simple-input>

                       <wysiwyg v-model="form.confirmation_content[lang]" v-show="lang === selectedLanguage" with-links
                                :placeholders="interviewPlaceholders.placeholdersInvitationInterviewConfirmed">
                           {{ $t('interview.confirmation.content') }} <span v-if="languages.length > 1">({{ lang }})</span>
                       </wysiwyg>
                   </template>
<!--                    <template v-else>-->
                        <label v-show="lang === selectedLanguage"
                               :for="'file-' + _uid"
                               class="mb-8 mt-16 text-body-sm flex items-center">
                            <span class="first-letter:uppercase font-semibold text-black">
                                {{ $t('general.attachments') }} <span v-if="languages.length > 1">({{ lang }})</span>
                            </span>
                        </label>

                        <file-upload
                            v-show="lang === selectedLanguage"
                            ref="fileUpload"
                            v-model="form.queued_attachments[lang]"
                            multiple
                            @removed="removeFile">

                            <!--show exsisting files-->
                            <ul v-if="form.attachments.length" class="rounded-sm overflow-hidden w-full mb-16">
                                <li
                                    v-for="attachment in form.attachments"
                                    v-show="attachment.locale === selectedLanguage"
                                    :key="'attachment-' + attachment.id"
                                    class="odd:bg-primary-5 relative"
                                >
                                    <a :download="!extensions.includes(attachment.extension) ? attachment.original_name : null"
                                       :href="attachment.url"
                                       :target="extensions.includes(attachment.extension) ? '_blank' : null"
                                       class="group flex items-center p-16 cursor-pointer"
                                    >
                                        <div class="mr-auto font-semibold group-hover:text-primary trans-ease">{{ attachment.original_name }}</div>
                                        <div class="pl-16">
                                            <div class="h-32 w-32 flex items-center justify-center rounded-sm text-primary-80 trans-ease group-hover:text-primary mr-32">
                                                <svg-icon class="max-w-16 max-h-16" type="download"></svg-icon>
                                            </div>
                                        </div>
                                    </a>

                                    <div class="absolute right-0 inset-y-0 mr-16 flex items-center pointer-events-none">
                                        <button class="text-primary-80 hover:text-semantic-error cursor-pointer trans-ease pointer-events-auto h-32 w-32 flex items-center justify-center rounded-sm"
                                                @click="confirmDeleteAttachment(attachment)">
                                            <svg-icon class="max-w-16 max-h-16" type="delete"></svg-icon>
                                        </button>
                                    </div>
                                </li>
                            </ul>
                        </file-upload>
<!--                    </template>-->
                </div>
            </translatable>
        </div>

        <popup-modal-confirm
            v-if="deletingAttachment"
            :confirmText="$t('general.delete_subject', {subject: $t('general.attachment')})"
            :message="$t('general.confirm_delete_subject', {subject: deletingAttachment.original_name})"
            :working="performingDeleteAttachmentRequest"
            @close="cancelDeleteAttachment"
            @confirm="deleteAttachment(deletingAttachment)"
        />

    </edit-resource>
</template>

<script>
import EditResource from './EditResource.vue';
import Input from '../forms/Input.vue';
import Form from '../../etc/forms/Form';
import Wysiwyg from '../forms/Wysiwyg.vue';
import Translatable from '../forms/Translatable.vue';
import * as templates from '../../services/EmailTemplateService';
import EmailTemplate from '../../models/EmailTemplate';
import {normalizeJson, openableFileExtensionsInWebBrowser} from '../../../shared/helpers';
import Toggle from '../forms/Toggle.vue';
import FileUpload from '../forms/FileUpload';
import * as documents from '../../services/DocumentService';
import Document from '../../models/Document';
import SvgIcon from './SvgIcon';
import * as attachmentService from '../../services/AttachmentService';
import Attachment from '../../models/Attachment';
import PopupModalConfirm from './PopupModalConfirm';
import Location from '../../models/Location';
import {mapState} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';

function getFields(languages) {

    let attachments = {};

    for (let lang of languages) {
        attachments[lang] = [];
    }

    return [
        {name: 'queued_attachments', value: {...attachments}},
        {name: 'attachments', value: {}},
        {name: 'name', value: {}, translatable: true},
        {name: 'subject', value: {}, translatable: true},
        {name: 'content', value: {}, translatable: true},
        {name: 'confirmation_subject', value: {}, translatable: true},
        {name: 'confirmation_content', value: {}, translatable: true},
        {name: 'type', value: 'general'},
        {name: 'default', value: false},
    ];
}

export default {
    components: {
        'edit-resource': EditResource,
        'simple-input': Input,
        'wysiwyg': Wysiwyg,
        'translatable': Translatable,
        'toggle': Toggle,
        'file-upload': FileUpload,
        'svg-icon': SvgIcon,
        'popup-modal-confirm': PopupModalConfirm,
    },
    props: {
        template: Object,
        type: {
            type: String,
            default: 'general',
            validator: value => ['general', 'auto_reply', 'retention_extension', 'interview', 'interim'].includes(value),
        },
    },
    setup() {
        const mainStore = useMainStore();
        return {mainStore};
    },
    data: function () {
        return {
            form: this.generateForm(),
            extensions: openableFileExtensionsInWebBrowser,
            deletingAttachment: null,
            performingDeleteAttachmentRequest: false,
        };
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany']),
        title() {
            return this.$t(this.template ? 'general.edit_subject' : 'general.add_subject', {subject: this.$t('settings.email-templates.template')});
        },
        warningTabs() {
            let languages = this.currentCompany.jobsite.active_languages;
            let errorLang = [];

            for (let index in languages) {
                if (!this.form.data.name[languages[index]]) {
                    errorLang.push(languages[index]);
                }
                if (!this.form.data.subject[languages[index]]) {
                    errorLang.push(languages[index]);
                }
                if (!this.form.data.content[languages[index]] || this.form.data.content[languages[index]].match(/<p><\/p>/g)) {
                    errorLang.push(languages[index]);
                }
                if (this.form.type === 'interview') {
                    if (!this.form.data.confirmation_subject[languages[index]]) {
                        errorLang.push(languages[index]);
                    }
                    if (!this.form.data.confirmation_content[languages[index]] || this.form.data.confirmation_content[languages[index]].match(/<p><\/p>/g)) {
                        errorLang.push(languages[index]);
                    }
                }
            }
            return errorLang;
        },
        placeholders() {
            let placeholders = [];
            switch (this.type) {
                case 'interim':
                    placeholders.push('company', 'job');
                    break;
                case 'auto_reply':
                    placeholders.push('company', 'application');
                    break;
               default:
                    placeholders.push('company', 'application', 'user');
                    break;
            }
            return placeholders
        },
        interviewPlaceholders(){
            return { placeholdersInterviewInvitation: ['application', 'company', 'user', 'interviewInvitation'], placeholdersInvitationInterviewConfirmed: ['application', 'company', 'user', 'interviewInvitation', 'interviewInvitationConfirmed'] }
        }
    },
    methods: {
        generateForm() {
            let fields = getFields(this.mainStore.currentCompany.jobsite.active_languages);

            if (this.template) {
                fields.forEach((field, index) => {
                    if (field.name !== 'queued_attachments') {
                        fields[index].value = JSON.parse(JSON.stringify(this.template[field.name]));
                    }
                });
            } else {
                fields[fields.findIndex(field => field.name === 'type')].value = this.type;
            }

            return new Form(fields);
        },

        async saveTemplate() {

            let newTemplateObj;

            if (this.template && this.template.id) {
                newTemplateObj = await this.updateTemplate();
            } else {
                newTemplateObj = await this.createTemplate();
            }

            if (newTemplateObj.default) {
                //Set default for all other templates to false
                useRepo(EmailTemplate)
                    .where((template) => {
                        return template.type == newTemplateObj.type && template.id != newTemplateObj.id && template.default;
                    })
                    .update({default: false});
            }

            this.$emit('close');
        },
        updateTemplate() {
            return this.form.submit(data => templates.update(this.template.id, {
                data: {attributes: data},
            })).then(result => {
                return useRepo(EmailTemplate).save(normalizeJson(result));
            });
        },
        createTemplate() {
            return this.form.submit(data => templates.store({
                data: {attributes: data},
            })).then(result => {
                return useRepo(EmailTemplate).save(normalizeJson(result));
            });
        },

        confirmDeleteAttachment(attachment) {
            this.deletingAttachment = attachment;
        },
        cancelDeleteAttachment() {
            this.deletingAttachment = null;
        },
        deleteAttachment(attachment) {
            this.performingDeleteAttachmentRequest = true;

            attachmentService.destroyNewsletterTemplate(attachment.id)
                .then(() => useRepo(Attachment).destroy(attachment.id))
                .then(() => {
                    this.form.attachments = this.form.attachments.filter(att => att.id !== attachment.id);
                    this.template.attachments = this.template.attachments.filter(att => att.id !== attachment.id);

                    this.deletingAttachment = null;
                    this.performingDeleteAttachmentRequest = false;
                });
        },
        removeFile({rawFile}) {
            if (!rawFile.id) {
                return;
            }

            documents.destroy(rawFile.id)
                .then(() => useRepo(Document).destroy(rawFile.id));
        },
    },
};
</script>
